import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Chart, ArcElement, Tooltip, Legend } from 'chart.js';
import { PieChartColors } from '../../configs';



Chart.register(
    ArcElement,
    Tooltip,
    Legend,
    ChartDataLabels
);

export interface PieChartItem {
    name: string;
    value: number;
    percentage: number;
    clickable?: boolean;
}


@Component({
    selector: 'app-pie-chart',
    templateUrl: './pie-chart.component.html',
    styleUrl: './pie-chart.component.scss',
    standalone: false
})
export class PieChartComponent implements OnChanges, AfterViewInit, OnDestroy {

    @Input()
    items: any[] = [];

    @Input()
    activeIndex: number | null = null;

    @Output()
    pieSelect = new EventEmitter<{
        item: any;
        index: number;
    }>();

    @ViewChild('chartCanvas')
    chartCanvas!: ElementRef<HTMLCanvasElement>;

    chart!: Chart;

    hoveredIndex: number | null = null;

    ngAfterViewInit(): void {
        this.createChart();
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (
            this.chart &&
            (
                changes['items'] ||
                changes['activeIndex']
            )
        ) {

            this.updateChart();
        }
    }

    createChart(): void {

        const ctx =
            this.chartCanvas.nativeElement.getContext('2d');

        if (!ctx) {
            return;
        }

        this.chart = new Chart(ctx, {

            type: 'pie',

            data: {
                labels: this.items.map(item => item.name),

                datasets: [
                    {
                        data: this.items.map(
                            item => item.percentage
                        ),

                        backgroundColor:
                            this.getBackgroundColors(),

                        borderColor: '#FFFFFF',

                        borderWidth: 2,

                        hoverOffset: 0,

                        offset:
                            this.getOffsets() as unknown as number
                    }
                ]
            },

            options: {

                responsive: true,

                maintainAspectRatio: false,

                layout: {
                    padding: {
                        top: 30,
                        right: 50,
                        bottom: 50,
                        left: 50
                    }
                },

                animation: {
                    duration: 300
                },

                plugins: {

                    legend: {
                        display: false
                    },

                    tooltip: {

                        enabled: true,

                        displayColors: false,

                        backgroundColor: '#111827',

                        padding: 10,

                        callbacks: {

                            title: () => '',

                            label: (tooltipItem) => {

                                const item =
                                    this.items[tooltipItem.dataIndex];

                                const hasCategories =
                                    !!item?.categories?.length;

                                return hasCategories
                                    ? 'Click for details'
                                    : '';
                            }
                        },

                        filter: (tooltipItem) => {

                            const item =
                                this.items[tooltipItem.dataIndex];

                            return !!item?.categories?.length;
                        }
                    },

                    datalabels: {
                        display: false
                    }
                },

                onHover: (_, elements) => {

                    const canvas =
                        this.chartCanvas.nativeElement;

                    if (!elements.length) {

                        this.hoveredIndex = null;

                        canvas.style.cursor = 'default';

                        this.updateChart();

                        return;
                    }

                    const index = elements[0].index;

                    const item =
                        this.items[index];

                    const hasCategories =
                        !!item?.categories?.length;

                    canvas.style.cursor =
                        hasCategories
                            ? 'pointer'
                            : 'default';

                    this.hoveredIndex =
                        hasCategories
                            ? index
                            : null;

                    this.updateChart();
                }
            },

            plugins: [
                {
                    id: 'outsideLabels',

                    afterDraw: (chart) => {

                        const { ctx } = chart;

                        const meta =
                            chart.getDatasetMeta(0);

                        meta.data.forEach(
                            (slice: any, index: number) => {

                                const item =
                                    this.items[index];

                                const angle =
                                    (
                                        slice.startAngle +
                                        slice.endAngle
                                    ) / 2;

                                const x = slice.x;

                                const y = slice.y;

                                const radius =
                                    slice.outerRadius;

                                const startX =
                                    x +
                                    Math.cos(angle) *
                                    radius;

                                const startY =
                                    y +
                                    Math.sin(angle) *
                                    radius;

                                const midX =
                                    x +
                                    Math.cos(angle) *
                                    (radius + 28);

                                const midY =
                                    y +
                                    Math.sin(angle) *
                                    (radius + 28);

                                const isRightSide =
                                    Math.cos(angle) >= 0;

                                const endX =
                                    midX +
                                    (
                                        isRightSide
                                            ? 40
                                            : -40
                                    );

                                ctx.save();

                                ctx.strokeStyle =
                                    '#D7E3F4';

                                ctx.lineWidth = 1;

                                ctx.beginPath();

                                ctx.moveTo(
                                    startX,
                                    startY
                                );

                                ctx.lineTo(
                                    midX,
                                    midY
                                );

                                ctx.lineTo(
                                    endX,
                                    midY
                                );

                                ctx.stroke();

                                ctx.textAlign =
                                    isRightSide
                                        ? 'left'
                                        : 'right';

                                ctx.textBaseline =
                                    'middle';

                                ctx.fillStyle =
                                    '#111827';
                                const appFontFamily = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
                                ctx.font =
                                    this.activeIndex === index
                                        ? `500 14px ${appFontFamily}`
                                        : `300 14px ${appFontFamily}`;

                                ctx.fillText(
                                    item.name,
                                    isRightSide
                                        ? endX + 6
                                        : endX - 6,
                                    midY - 8
                                );

                                ctx.fillStyle =
                                    PieChartColors[index];

                                ctx.fillText(
                                    `${item.percentage}%`,
                                    isRightSide
                                        ? endX + 6
                                        : endX - 6,
                                    midY + 12
                                );

                                ctx.restore();
                            }
                        );
                    }
                }
            ]
        });
    }

    updateChart(): void {

        this.chart.data.labels =
            this.items.map(item => item.name);

        this.chart.data.datasets[0].data =
            this.items.map(item => item.percentage);

        this.chart.data.datasets[0].backgroundColor =
            this.getBackgroundColors();

        (
            this.chart.data.datasets[0] as any
        ).offset =
            this.getOffsets();

        this.chart.update();
    }

    onChartClick(event: MouseEvent): void {

        const points =
            this.chart.getElementsAtEventForMode(
                event,
                'nearest',
                { intersect: true },
                true
            );

        if (!points.length) {
            return;
        }

        const index = points[0].index;

        const item =
            this.items[index];

        if (!item?.categories?.length) {
            return;
        }

        this.pieSelect.emit({
            item,
            index
        });
    }

    ngOnDestroy(): void {
        this.destroyChart();
    }

    private destroyChart() {
        if (this.chart) {
            this.chart.destroy();
            this.chart = null;
        }
    }

    private getBackgroundColors(): string[] {

        return this.items.map((item, index) => {

            const color =
                PieChartColors[index];

            const hasCategories =
                !!item?.categories?.length;

            // selected state
            if (this.activeIndex !== null) {

                if (this.activeIndex === index) {
                    return color;
                }

                return this.hexToRgba(color, 0.35);
            }

            // hover state
            if (
                this.hoveredIndex !== null &&
                hasCategories
            ) {

                return this.hoveredIndex === index
                    ? this.hexToRgba(color, 0.7)
                    : color;
            }

            return color;
        });
    }

    private getOffsets(): number[] {

        return this.items.map((_, index) => {

            return this.activeIndex === index
                ? 18
                : 0;
        });
    }

    private hexToRgba(
        hex: string,
        opacity: number
    ): string {

        const sanitized =
            hex.replace('#', '');

        const r =
            parseInt(sanitized.substring(0, 2), 16);

        const g =
            parseInt(sanitized.substring(2, 4), 16);

        const b =
            parseInt(sanitized.substring(4, 6), 16);

        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
}